<script setup>
import { ref, computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
const { t } = useI18n();
const store = useStore();

const { dateRange, isLoading: parentLoading } = inject('analyticsData');

const isLoading = ref(false);
const metaData = ref(null);
const currentPage = ref(1);

const accountId = computed(() => store.getters.getCurrentAccountId);

async function fetchMetaAds() {
  if (!accountId.value) return;
  isLoading.value = true;

  try {
    const params = {};
    if (dateRange.value) {
      params.since = dateRange.value.from;
      params.until = dateRange.value.to;
    }
    params.page = currentPage.value;

    // Use global axios (has auth interceptor with user_access_token)
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/meta_ads`,
      { params }
    );
    metaData.value = data;
  } catch (error) {
    console.error('[MetaAds] Error:', error);
  } finally {
    isLoading.value = false;
  }
}

const totalLeads = computed(() => metaData.value?.summary?.total_leads || 0);
const byAd = computed(() => {
  const ads = metaData.value?.summary?.by_ad || {};
  return Object.entries(ads).map(([headline, count]) => ({ headline, count }));
});
const leads = computed(() => metaData.value?.leads || []);
const pagination = computed(() => metaData.value?.pagination || {});

const statusLabel = (status) => {
  const map = { open: 'Aberta', resolved: 'Resolvida', pending: 'Pendente' };
  return map[status] || status;
};

const statusColor = (status) => {
  const map = {
    open: 'bg-n-blue-3 text-n-blue-11',
    resolved: 'bg-n-teal-3 text-n-teal-11',
    pending: 'bg-n-amber-3 text-n-amber-11',
  };
  return map[status] || 'bg-n-alpha-2 text-n-slate-11';
};

function goToPage(page) {
  currentPage.value = page;
  fetchMetaAds();
}

function openConversation(id) {
  const url = `/app/accounts/${accountId.value}/conversations/${id}`;
  window.open(url, '_blank');
}

onMounted(fetchMetaAds);

// Refetch when date range changes
import { watch } from 'vue';
watch(dateRange, () => {
  currentPage.value = 1;
  fetchMetaAds();
}, { deep: true });
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-16">
      <span class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10" />
    </div>

    <template v-else-if="metaData">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Total Leads -->
        <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
          <div class="flex items-center gap-3 mb-3">
            <div class="w-10 h-10 rounded-lg bg-n-blue-3 flex items-center justify-center">
              <span class="i-lucide-megaphone text-lg text-n-blue-11" />
            </div>
            <span class="text-sm font-medium text-n-slate-11">Total de Leads via Ads</span>
          </div>
          <div class="text-3xl font-bold text-n-slate-12">{{ totalLeads }}</div>
        </div>

        <!-- By Status -->
        <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
          <div class="flex items-center gap-3 mb-3">
            <div class="w-10 h-10 rounded-lg bg-n-teal-3 flex items-center justify-center">
              <span class="i-lucide-bar-chart-3 text-lg text-n-teal-11" />
            </div>
            <span class="text-sm font-medium text-n-slate-11">Por Status</span>
          </div>
          <div class="flex gap-4 mt-2">
            <div>
              <div class="text-xl font-bold text-n-blue-11">{{ metaData.summary.by_status?.open || 0 }}</div>
              <div class="text-xs text-n-slate-10">Abertas</div>
            </div>
            <div>
              <div class="text-xl font-bold text-n-teal-11">{{ metaData.summary.by_status?.resolved || 0 }}</div>
              <div class="text-xs text-n-slate-10">Resolvidas</div>
            </div>
            <div>
              <div class="text-xl font-bold text-n-amber-11">{{ metaData.summary.by_status?.pending || 0 }}</div>
              <div class="text-xs text-n-slate-10">Pendentes</div>
            </div>
          </div>
        </div>

        <!-- Top Ads -->
        <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
          <div class="flex items-center gap-3 mb-3">
            <div class="w-10 h-10 rounded-lg bg-n-violet-3 flex items-center justify-center">
              <span class="i-lucide-target text-lg text-n-violet-11" />
            </div>
            <span class="text-sm font-medium text-n-slate-11">Top Anúncios</span>
          </div>
          <div v-if="byAd.length" class="space-y-2 mt-2">
            <div v-for="ad in byAd.slice(0, 3)" :key="ad.headline" class="flex items-center justify-between">
              <span class="text-xs text-n-slate-11 truncate max-w-[180px]">{{ ad.headline || 'Sem título' }}</span>
              <span class="text-sm font-semibold text-n-slate-12">{{ ad.count }}</span>
            </div>
          </div>
          <div v-else class="text-sm text-n-slate-10 mt-2">Nenhum anúncio rastreado</div>
        </div>
      </div>

      <!-- Leads Table -->
      <div class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden">
        <div class="px-5 py-4 border-b border-n-weak">
          <h3 class="text-sm font-semibold text-n-slate-12">Leads de Meta Ads</h3>
          <p class="text-xs text-n-slate-10 mt-1">Conversas originadas de anúncios Facebook/Instagram</p>
        </div>

        <div v-if="leads.length" class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-n-alpha-1">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11">Contato</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11">Anúncio</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11">Inbox</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11">Status</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11">Atendente</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11">Data</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak/50">
              <tr
                v-for="lead in leads"
                :key="lead.conversation_id"
                class="hover:bg-n-alpha-1 transition-colors cursor-pointer"
                @click="openConversation(lead.conversation_id)"
              >
                <td class="px-4 py-3">
                  <div class="font-medium text-n-slate-12">{{ lead.contact_name || 'Desconhecido' }}</div>
                  <div class="text-xs text-n-slate-10">{{ lead.contact_phone }}</div>
                </td>
                <td class="px-4 py-3">
                  <div class="text-n-slate-12 max-w-[200px] truncate">{{ lead.ad_headline || '-' }}</div>
                  <div v-if="lead.ad_source" class="text-xs text-n-slate-10">{{ lead.ad_source }}</div>
                </td>
                <td class="px-4 py-3 text-n-slate-11">{{ lead.inbox_name }}</td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="statusColor(lead.status)"
                  >
                    {{ statusLabel(lead.status) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-n-slate-11">{{ lead.assignee || '-' }}</td>
                <td class="px-4 py-3 text-xs text-n-slate-10">
                  {{ new Date(lead.created_at).toLocaleDateString('pt-BR') }}
                </td>
                <td class="px-4 py-3">
                  <span class="i-lucide-external-link text-n-slate-10 hover:text-n-blue-11" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty state -->
        <div v-else class="flex flex-col items-center justify-center py-16 text-center">
          <span class="i-lucide-megaphone text-4xl text-n-slate-9 mb-3" />
          <h4 class="text-sm font-medium text-n-slate-11">Nenhum lead de Meta Ads</h4>
          <p class="text-xs text-n-slate-10 mt-1 max-w-sm">
            Quando alguém clicar num anúncio Click-to-WhatsApp no Facebook ou Instagram, os dados da campanha aparecerão aqui automaticamente.
          </p>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.total_pages > 1" class="flex items-center justify-between px-5 py-3 border-t border-n-weak">
          <span class="text-xs text-n-slate-10">
            Página {{ pagination.page }} de {{ pagination.total_pages }} ({{ pagination.total }} leads)
          </span>
          <div class="flex gap-2">
            <button
              :disabled="pagination.page <= 1"
              class="px-3 py-1 text-xs rounded-lg border border-n-weak hover:bg-n-alpha-1 disabled:opacity-50"
              @click="goToPage(pagination.page - 1)"
            >
              Anterior
            </button>
            <button
              :disabled="pagination.page >= pagination.total_pages"
              class="px-3 py-1 text-xs rounded-lg border border-n-weak hover:bg-n-alpha-1 disabled:opacity-50"
              @click="goToPage(pagination.page + 1)"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- No data -->
    <div v-else class="flex flex-col items-center justify-center py-16 text-center">
      <span class="i-lucide-megaphone text-4xl text-n-slate-9 mb-3" />
      <h4 class="text-sm font-medium text-n-slate-11">Meta Ads Tracking</h4>
      <p class="text-xs text-n-slate-10 mt-1">Carregando dados...</p>
    </div>
  </div>
</template>
